import React, { useEffect, useState } from "react";
import { MessageCircle, CheckCheck } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { fmtMoney } from "@/components/EventCard";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";

type Cur = { fees: string; exemptions: string; payments: string; closing: string };
type Row = { account_id: string; business_name: string; phone: string; model: string; currencies: Record<string, Cur>; sent?: { at: string; by: string } | null };

const waNum = (p: string) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("07")) d = "964" + d.slice(1);
  return d;
};
const money = (amount: string | number, currency: string) => fmtMoney({ amount: String(amount), currency } as any);

const monthName = (m: string, lang: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString(lang === "ar" ? "ar-IQ" : "en-GB", { month: "long", year: "numeric" });

const buildMsg = (r: Row, month: string, t: (k: string) => string) => {
  const lines = Object.entries(r.currencies).map(([c, v]) => t("ms.msgLine")
    .replace("{fees}", money(Number(v.fees) - Number(v.exemptions), c)).replace("{paid}", money(v.payments, c)).replace("{due}", money(v.closing, c)));
  return [t("ms.msgHead").replace("{name}", r.business_name).replace("{month}", month), ...(lines.length ? lines : [t("ms.msgNone")]), t("ms.msgFoot")].join("\n");
};

export const MonthlySummaries = () => {
  const { t, lang } = usePrefs();
  const [month, setMonth] = useState("");
  const [data, setData] = useState<{ month: string; rows: Row[] } | null>(null);
  const load = (m = month) => api.get("/admin/monthly-summaries", { params: m ? { month: m } : {} }).then((r) => { setData(r.data); setMonth(r.data.month); }).catch(() => {});
  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const markSent = (r: Row) => api.post(`/admin/monthly-summaries/${r.account_id}/sent`, { month: data!.month }).then(() => load(data!.month)).catch(() => {});
  return (
    <section data-testid="monthly-summaries" className="rounded-3xl border bg-card p-5 mb-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-display text-lg font-black">{t("ms.title")}</h2><p className="text-sm text-muted-foreground">{t("ms.sub")}</p></div>
        <Input type="month" value={month} onChange={(e: any) => e.target.value && load(e.target.value)} data-testid="monthly-summaries-month" className="h-10 rounded-full w-44" dir="ltr" />
      </div>
      {data && !data.rows.length && <p className="text-sm text-muted-foreground">{t("ms.empty")}</p>}
      <div className="grid gap-2">
        {data?.rows.map((r) => (
          <div key={r.account_id} data-testid={`monthly-summary-${r.account_id}`} className="rounded-2xl bg-muted/60 p-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[12rem]">
              <p className="font-bold">{r.business_name} <span className="text-xs font-semibold text-muted-foreground">· {t(`fm.${r.model}`)}</span></p>
              <p className="text-xs text-muted-foreground">{Object.keys(r.currencies).length ? Object.entries(r.currencies).map(([c, v]) => <span key={c} className="me-2">{t("ms.due")}: <bdi>{money(v.closing, c)}</bdi></span>) : t("ms.msgNone")}</p>
              {r.sent && <p data-testid={`monthly-summary-sent-${r.account_id}`} className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" />{t("ms.sent").replace("{by}", r.sent.by).replace("{at}", fmtDateTime(r.sent.at, lang))}</p>}
            </div>
            {r.phone ? (
              <a href={`https://wa.me/${waNum(r.phone)}?text=${encodeURIComponent(buildMsg(r, monthName(data.month, "ar"), t))}`} target="_blank" rel="noreferrer" onClick={() => markSent(r)}
                data-testid={`monthly-summary-wa-${r.account_id}`} className="h-9 px-4 rounded-full text-xs font-bold inline-flex items-center gap-1.5 bg-[#25D366] text-white hover:bg-[#1ebe5b] active:scale-95 transition-transform">
                <MessageCircle className="h-4 w-4" />{t(r.sent ? "ms.resend" : "ms.send")}
              </a>
            ) : <span className="text-xs text-muted-foreground">{t("reg.noPhone")}</span>}
          </div>
        ))}
      </div>
    </section>
  );
};
